import React, { useState, useEffect, useRef, useId } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Envelope, Clock, GitBranch, Lightning, Target, Presentation,
  Users, Flask, ThumbsUp, User, ChatText, Palette, TextAa, Cards,
  ThumbsDown, SkipForward, Sparkle, ArrowRight, FileText, Download, Paperclip,
  ArrowClockwise, ArrowUUpLeft, Eye, TreeView, Gauge, WarningCircle
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { presentationsApi, materialsApi } from '@/lib/api'
import { emailBodyToHtml } from '@/lib/utils'
import { dangerBannerClass, bannerTextStyle } from '@/lib/styleKit'
import { handleTabListKeyDown } from '@/lib/rovingTabs'
import type { PlaygroundStep, GeneratedEmail, GeneratedDeck, UsageEntry } from './usePlaygroundWalker'
import type { ReplyScenario } from '@/types/workflow'
import { countAttachedMaterials } from './attachedMaterialsCount'
import { TestNodePanel } from '../TestNodePanel'

interface Props {
  step: PlaygroundStep
  isGenerating: boolean
  onGenerate: () => void
  onAdvance: () => void
  onSelectBranch: (choice: string) => void
  onSetReplyScenario: (scenario: ReplyScenario | undefined) => void
  onCompleteAndAdvance: () => void
  isComplete: boolean
  attachedDeck?: GeneratedDeck
  onDownloadDeckPdf?: () => void
  onDownloadDeck?: () => void
  contactId?: number
  /** True when the user has navigated back to review/re-run a previous step. */
  isViewingPast?: boolean
  onReRun?: () => void
  onBackToCurrent?: () => void
}

export const PlaygroundStepDetail: React.FC<Props> = ({
  step, isGenerating, onGenerate, onAdvance, onSelectBranch,
  onSetReplyScenario, onCompleteAndAdvance, isComplete, attachedDeck, onDownloadDeckPdf, onDownloadDeck, contactId,
  isViewingPast = false, onReRun, onBackToCurrent,
}) => {
  const [activeTab, setActiveTab] = useState<'preview' | 'discussion'>('preview')
  const [previewMode, setPreviewMode] = useState<'blocks' | 'full'>('blocks')
  // One id root per mounted panel; the two tab bars derive distinct prefixes
  // from it so their tab/panel ids stay unique even side by side.
  const uid = useId()
  // The node whose real Run Test last succeeded. Enrichment is never auto-run in
  // the playground, so this is what tells the step it has actually executed and
  // the button should read "Next step", not "Skip & Continue" (AB#22529).
  const [testedNodeId, setTestedNodeId] = useState<string | null>(null)

  // Both view toggles are single pieces of state shared by every step's detail,
  // so leaving one on "AI Discussion" / "Full Email" handed that view to the
  // *next* step the user opened. Keyed on nodeId rather than the step index —
  // indices are reused as the user navigates back and forth.
  useEffect(() => {
    setActiveTab('preview')
    setPreviewMode('blocks')
  }, [step.nodeId])

  if (isComplete) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5 bg-emerald-500/10 border border-emerald-500/20 dark:bg-emerald-500/[0.12] dark:border-emerald-500/25">
          <Target className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h2 className="text-lg font-emphasis text-foreground mb-2" style={{ letterSpacing: '-0.24px' }}>Simulation Complete</h2>
        <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
          The workflow has been fully simulated. Review the timeline on the left to see all generated content and branch decisions.
        </p>
      </div>
    )
  }

  const renderStepContent = () => {
    switch (step.nodeType) {
      case 'trigger':
        return <TriggerDetail step={step} />
      case 'sendEmail':
        return (
          <SendEmailDetail
            step={step}
            isGenerating={isGenerating}
            onGenerate={onGenerate}
            onAdvance={onAdvance}
            onSetReplyScenario={onSetReplyScenario}
            onSkip={onCompleteAndAdvance}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            previewMode={previewMode}
            setPreviewMode={setPreviewMode}
            attachedDeck={attachedDeck}
            readOnly={isViewingPast}
            tabIdPrefix={`${uid}-email`}
          />
        )
      case 'generateDeck':
        return (
          <GenerateDeckDetail
            step={step}
            isGenerating={isGenerating}
            onGenerate={onGenerate}
            onAdvance={onAdvance}
            onSkip={onCompleteAndAdvance}
            onDownloadDeckPdf={onDownloadDeckPdf}
            onDownloadDeck={onDownloadDeck}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            readOnly={isViewingPast}
            tabIdPrefix={`${uid}-deck`}
          />
        )
      case 'delay':
        return <DelayDetail step={step} onSkip={onCompleteAndAdvance} readOnly={isViewingPast} />
      case 'rateLimit':
        return <RateLimitDetail step={step} onContinue={onCompleteAndAdvance} readOnly={isViewingPast} />
      case 'condition':
        return <ConditionDetail step={step} onSelectBranch={onSelectBranch} readOnly={isViewingPast} />
      case 'abTest':
        return <ABTestDetail step={step} onSelectBranch={onSelectBranch} readOnly={isViewingPast} />
      case 'branchByField':
        return <BranchByFieldDetail step={step} onSelectBranch={onSelectBranch} onContinue={onCompleteAndAdvance} readOnly={isViewingPast} />
      case 'aiDecision':
        return <AiDecisionDetail step={step} onSelectBranch={onSelectBranch} onContinue={onCompleteAndAdvance} readOnly={isViewingPast} />
      case 'action':
        return <ActionDetail step={step} onContinue={onCompleteAndAdvance} readOnly={isViewingPast} />
      case 'enrichContacts':
        return <EnrichDetail step={step} onContinue={onCompleteAndAdvance} readOnly={isViewingPast} enriched={testedNodeId === step.nodeId} />
      case 'goal':
        return <GoalDetail step={step} />
      default:
        return <GenericDetail step={step} onContinue={onCompleteAndAdvance} readOnly={isViewingPast} />
    }
  }

  // Re-running only makes sense for steps that produce content.
  const canReRun = step.nodeType === 'sendEmail' || step.nodeType === 'generateDeck'

  // Which retry path is correct follows the same test the walker's reducer uses
  // to restore the status: a step that already holds content failed a *re-run*,
  // which onReRun replaces in place; a step with none failed its first attempt,
  // which onGenerate must own so the email still joins the sequence chain.
  const retryFailedStep = (step.generatedEmail || step.generatedDeck) ? onReRun : onGenerate

  return (
    <>
      {isViewingPast && (
        <div className="mx-6 mt-4 flex items-center gap-2 rounded-lg border border-[#5e6ad2]/20 bg-[#5e6ad2]/[0.06] px-3 py-2 text-xs text-[#5e6ad2] dark:border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.12] dark:text-[#a5b4fc]">
          <Eye className="w-3.5 h-3.5 flex-shrink-0" />
          <span>You are viewing a previous step. Re-run it, or return to where the simulation left off.</span>
        </div>
      )}
      {/* Failure surface. The timeline row owns the role="alert" announcement —
          this banner is the place to act on it, so it carries the message and
          the retry, not a second live region. */}
      {step.error && (
        <div className={`${dangerBannerClass} mx-6 mt-4`}>
          <WarningCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 min-w-0 break-words" style={bannerTextStyle}>{step.error}</span>
          {retryFailedStep && (
            <Button
              variant="outline"
              size="sm"
              onClick={retryFailedStep}
              loading={isGenerating}
              icon={<ArrowClockwise className="w-3.5 h-3.5" />}
              className="flex-shrink-0"
            >
              Retry
            </Button>
          )}
        </div>
      )}
      {renderStepContent()}
      {/* Real "Run Test" panel — sends a real email / runs the real integration,
          which is distinct from the in-step Preview. Shown for every testable
          node (Send Email included) as a single always-open "Run Test" button
          (no "Test This Node" accordion). generateDeck is excluded because its
          own "Generate Deck / Preview Material" CTA already runs it. TestNodePanel
          renders nothing for non-testable node types. */}
      {step.nodeType !== 'generateDeck' && (
        <div className="px-6 pb-4">
          <TestNodePanel
            key={step.nodeId}
            onResult={({ success }) => setTestedNodeId(success ? step.nodeId : null)}
            nodeType={step.nodeType}
            nodeData={step.nodeData}
            contactId={contactId}
            alwaysOpen
            pregeneratedEmail={step.nodeType === 'sendEmail' && step.generatedEmail ? {
              subject: step.generatedEmail.subject,
              body: step.generatedEmail.body,
            } : undefined}
            deckAttachment={attachedDeck ? {
              download_url: attachedDeck.download_url,
              filename: attachedDeck.filename,
              material_id: attachedDeck.attached_material_id,
              slides: attachedDeck.slides,
              title: attachedDeck.title,
              color_theme: attachedDeck.color_theme,
              logo_url: attachedDeck.logo_url,
            } : undefined}
          />
        </div>
      )}
      {isViewingPast && (
        <div className="px-6 pb-6 flex items-center gap-2">
          {canReRun && (
            <Button
              variant="default"
              onClick={onReRun}
              loading={isGenerating}
              icon={<ArrowClockwise className="w-4 h-4" />}
              className="flex-1 h-11"
            >
              Re-run this step
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onBackToCurrent}
            icon={<ArrowUUpLeft className="w-4 h-4" />}
            className={canReRun ? 'flex-1 h-11' : 'w-full h-11'}
          >
            Back to current step
          </Button>
        </div>
      )}
    </>
  )
}

// -- Shared tab bar --
// The email ("Preview" / "AI Discussion") and deck ("Slides" / "AI Discussion")
// bars were byte-identical apart from that one label, and neither carried any
// tab semantics. Ids are per-instance (see the `useId` in PlaygroundStepDetail)
// so the two can be mounted in the same tree without colliding.
type StepTabId = 'preview' | 'discussion'

const tabDomId = (prefix: string, tab: StepTabId) => `${prefix}-tab-${tab}`
const panelDomId = (prefix: string, tab: StepTabId) => `${prefix}-panel-${tab}`

const StepTabs: React.FC<{
  idPrefix: string
  /** Accessible name for the tablist. */
  label: string
  /** Only the first tab's label differs between the two bars. */
  firstTabLabel: string
  activeTab: string
  onSelect: (t: StepTabId) => void
}> = ({ idPrefix, label, firstTabLabel, activeTab, onSelect }) => {
  const tabs: { id: StepTabId; label: string }[] = [
    { id: 'preview', label: firstTabLabel },
    { id: 'discussion', label: 'AI Discussion' },
  ]
  const activeIndex = tabs.findIndex(t => t.id === activeTab)
  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex gap-1 border-b border-border/50 dark:border-white/[0.08]"
      onKeyDown={(e) => handleTabListKeyDown(e, {
        activeIndex,
        count: tabs.length,
        onSelect: (i) => onSelect(tabs[i].id),
      })}
    >
      {tabs.map(t => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={tabDomId(idPrefix, t.id)}
          aria-selected={activeTab === t.id}
          aria-controls={panelDomId(idPrefix, t.id)}
          // Roving tabIndex — a tablist is one tab stop; arrows move within it.
          tabIndex={activeTab === t.id ? 0 : -1}
          onClick={() => onSelect(t.id)}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5e6ad2]/40 ${
            activeTab === t.id ? 'border-[#5e6ad2] text-[#5e6ad2] dark:border-[#a5b4fc] dark:text-[#a5b4fc]' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

// -- Selected branch read-out --
// The four branch-style details (Condition, A/B Test, Branch by Field, AI
// Decision) each carried a byte-identical copy of this line, all four leaning on
// the withdrawn Tailwind brand token instead of the brand hex pair (§46).
const SelectedBranchLine: React.FC<{ label: string; value: string; capitalize?: boolean }> = ({ label, value, capitalize }) => (
  <p className="text-xs text-center text-muted-foreground">
    {label}: <span className={`font-emphasis text-[#5e6ad2] dark:text-[#a5b4fc]${capitalize ? ' capitalize' : ''}`}>{value}</span>
  </p>
)

// -- Trigger --
const TriggerDetail: React.FC<{ step: PlaygroundStep }> = ({ step }) => (
  <DetailWrapper icon={<Lightning />} title={step.nodeData.label} subtitle="Trigger (auto-completed)">
    <div className="rounded-lg border border-border/50 bg-card dark:bg-white/[0.02] dark:border-white/[0.08] p-5 text-center">
      <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-3 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
        <Lightning className="w-6 h-6" />
      </div>
      <p className="text-sm font-medium text-foreground capitalize mb-1">
        {(step.nodeData.trigger_type || 'manual').replace(/_/g, ' ')}
      </p>
      <p className="text-xs text-muted-foreground">Workflow started automatically</p>
    </div>
  </DetailWrapper>
)

// -- Send Email --
const SendEmailDetail: React.FC<{
  step: PlaygroundStep
  isGenerating: boolean
  onGenerate: () => void
  onAdvance: () => void
  onSetReplyScenario: (s: ReplyScenario | undefined) => void
  onSkip: () => void
  activeTab: string
  setActiveTab: (t: 'preview' | 'discussion') => void
  previewMode: 'blocks' | 'full'
  setPreviewMode: (m: 'blocks' | 'full') => void
  attachedDeck?: GeneratedDeck
  readOnly?: boolean
  tabIdPrefix: string
}> = ({ step, isGenerating, onGenerate, onAdvance, onSetReplyScenario, onSkip, activeTab, setActiveTab, previewMode, setPreviewMode, attachedDeck, readOnly, tabIdPrefix }) => {
  const aiConfig = step.nodeData.ai_config
  const isAI = aiConfig?.generation_mode === 'ai'
  const email = step.generatedEmail
  // No AI discussion → no tab bar, so the preview must not claim to be a
  // tabpanel either (a tabpanel with no owning tab announces as orphaned).
  const hasDiscussion = !!email?.discussion?.length

  return (
    <DetailWrapper
      icon={<Envelope />}
      title={step.nodeData.label}
      subtitle={isAI ? 'AI-Generated Email' : 'Manual Email'}
    >
      {!email ? (
        <div className="space-y-4">
          {isAI && (
            <div className="rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] p-4">
              <div className="grid grid-cols-2 gap-3">
                <InfoCard icon={<ChatText className="w-3.5 h-3.5" />} label="Tone" value={aiConfig?.tone} />
                <InfoCard icon={<Target className="w-3.5 h-3.5" />} label="Purpose" value={aiConfig?.purpose} />
                <InfoCard icon={<Paperclip className="w-3.5 h-3.5" />} label="Materials" value={`${countAttachedMaterials(aiConfig?.material_ids, attachedDeck)} attached`} />
                <InfoCard icon={<User className="w-3.5 h-3.5" />} label="Contact fields" value={`${aiConfig?.contact_fields?.length || 0} selected`} />
              </div>
            </div>
          )}
          {!isAI && (
            <div className="rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] p-4">
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.06em]">Subject</span>
              <p className="text-sm font-medium text-foreground mt-1">{step.nodeData.subject || '(No subject)'}</p>
            </div>
          )}
          {attachedDeck && (
            <DeckAttachmentBadge deck={attachedDeck} />
          )}
          {!readOnly && (
            <Button
              variant="default"
              onClick={onGenerate}
              loading={isGenerating}
              icon={<Sparkle className="w-4 h-4" />}
              className="w-full h-11"
            >
              {isAI ? 'Generate Email' : 'Preview Email'}
            </Button>
          )}
          {!readOnly && (
            <Button
              variant="outline"
              onClick={onSkip}
              disabled={isGenerating}
              icon={<SkipForward className="w-4 h-4" />}
              className="w-full h-11"
            >
              Skip & Continue
            </Button>
          )}
        </div>
      ) : (
        <EmailPreview
          email={email}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          previewMode={previewMode}
          setPreviewMode={setPreviewMode}
          replyScenario={step.replyScenario}
          onSetReplyScenario={onSetReplyScenario}
          onAdvance={onAdvance}
          attachedDeck={attachedDeck}
          usageLog={step.usageLog}
          readOnly={readOnly}
          tabIdPrefix={tabIdPrefix}
          hasDiscussion={hasDiscussion}
        />
      )}
    </DetailWrapper>
  )
}

const EmailPreview: React.FC<{
  email: GeneratedEmail
  activeTab: string
  setActiveTab: (t: 'preview' | 'discussion') => void
  previewMode: 'blocks' | 'full'
  setPreviewMode: (m: 'blocks' | 'full') => void
  replyScenario?: ReplyScenario
  onSetReplyScenario: (s: ReplyScenario | undefined) => void
  onAdvance: () => void
  attachedDeck?: GeneratedDeck
  usageLog?: UsageEntry[]
  readOnly?: boolean
  tabIdPrefix: string
  hasDiscussion: boolean
}> = ({ email, activeTab, setActiveTab, previewMode, setPreviewMode, replyScenario, onSetReplyScenario, onAdvance, attachedDeck, usageLog, readOnly, tabIdPrefix, hasDiscussion }) => (
  <div className="space-y-4">
    {/* Tabs */}
    {hasDiscussion && (
      <StepTabs idPrefix={tabIdPrefix} label="Email preview" firstTabLabel="Preview" activeTab={activeTab} onSelect={setActiveTab} />
    )}

    <AnimatePresence mode="wait">
      {activeTab === 'preview' ? (
        <motion.div
          key="preview"
          role={hasDiscussion ? 'tabpanel' : undefined}
          id={hasDiscussion ? panelDomId(tabIdPrefix, 'preview') : undefined}
          aria-labelledby={hasDiscussion ? tabDomId(tabIdPrefix, 'preview') : undefined}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          {/* Block/Full toggle — a mode switch, not a tab bar (§48): solid brand
              fill on the active side is correct here. role="group" + aria-pressed
              is what makes the pair legible to a screen reader. */}
          {email.blocks && email.blocks.length > 0 && (
            <div className="flex rounded-md border border-border/60 dark:border-white/[0.08] overflow-hidden w-fit" role="group" aria-label="Email preview mode">
              <button
                type="button"
                aria-pressed={previewMode === 'blocks'}
                onClick={() => setPreviewMode('blocks')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  previewMode === 'blocks'
                    ? 'bg-[#5e6ad2] text-white'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
              >
                Block View
              </button>
              <button
                type="button"
                aria-pressed={previewMode === 'full'}
                onClick={() => setPreviewMode('full')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  previewMode === 'full'
                    ? 'bg-[#5e6ad2] text-white'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
              >
                Full Email
              </button>
            </div>
          )}

          {/* Email card */}
          <div className="rounded-lg border border-border/50 bg-background dark:bg-white/[0.02] dark:border-white/[0.08] overflow-hidden">
            {/* Subject bar */}
            <div className="px-4 py-3 bg-muted/40 dark:bg-white/[0.02] border-b border-border/50 dark:border-white/[0.06]">
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.06em]">Subject</span>
              <p className="text-sm font-emphasis text-foreground mt-0.5">{email.subject}</p>
            </div>
            {/* Body / Blocks */}
            <div className="p-4 max-h-80 overflow-y-auto">
              {previewMode === 'blocks' && email.blocks && email.blocks.length > 0 ? (
                <div className="space-y-2.5">
                  {email.blocks.map((block, idx) => (
                    <div key={idx} className="border border-border/50 dark:border-white/[0.08] rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 dark:bg-white/[0.03]">
                        <span className="text-[10px] font-emphasis text-muted-foreground uppercase tracking-[0.06em]">{block.name}</span>
                        <span className="text-[9px] text-muted-foreground font-mono">{block.content.split(/\s+/).length}w</span>
                      </div>
                      <div className="px-3 py-2 text-sm text-foreground leading-relaxed">
                        {block.content}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="prose prose-sm dark:prose-invert max-w-none text-sm [&>p]:mb-3 [&>p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: emailBodyToHtml(email.body) }} />
              )}
            </div>
          </div>
          {attachedDeck && (
            <DeckAttachmentBadge deck={attachedDeck} />
          )}
          {email.email_step && email.total_emails && (
            <p className="text-xs text-muted-foreground mt-2">
              Email {email.email_step} of {email.total_emails} in sequence
            </p>
          )}
        </motion.div>
      ) : (
        <motion.div
          key="discussion"
          role="tabpanel"
          id={panelDomId(tabIdPrefix, 'discussion')}
          aria-labelledby={tabDomId(tabIdPrefix, 'discussion')}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {email.discussion?.map((msg, i) => (
              <div key={i} className={`rounded-lg p-3 text-sm ${
                msg.role === 'user'
                  ? 'bg-[#5e6ad2]/[0.06] border border-[#5e6ad2]/15 dark:bg-[#5e6ad2]/[0.1] dark:border-[#5e6ad2]/20'
                  : 'bg-muted/60 border border-border/50 dark:bg-white/[0.03] dark:border-white/[0.06]'
              }`}>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-[0.06em] mb-1 block">{msg.role}</span>
                <p className="text-foreground whitespace-pre-wrap">{msg.content}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Usage breakdown */}
    {usageLog && usageLog.length > 0 && <UsageBreakdown usageLog={usageLog} />}

    {/* Reply simulation */}
    <div className="border-t border-border/50 dark:border-white/[0.06] pt-4">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        {readOnly ? 'Recipient reply (change to re-route the path):' : 'Simulate recipient reply:'}
      </p>
      <div className="flex gap-2 flex-wrap">
        <ReplyButton
          label="No Reply"
          icon={<SkipForward className="w-3.5 h-3.5" />}
          active={replyScenario === 'no_reply'}
          onClick={() => onSetReplyScenario(replyScenario === 'no_reply' ? undefined : 'no_reply')}
          variant="default"
        />
        <ReplyButton
          label="Positive"
          icon={<ThumbsUp className="w-3.5 h-3.5" />}
          active={replyScenario === 'positive'}
          onClick={() => onSetReplyScenario(replyScenario === 'positive' ? undefined : 'positive')}
          variant="green"
        />
        <ReplyButton
          label="Not Interested"
          icon={<ThumbsDown className="w-3.5 h-3.5" />}
          active={replyScenario === 'not_interested'}
          onClick={() => onSetReplyScenario(replyScenario === 'not_interested' ? undefined : 'not_interested')}
          variant="red"
        />
      </div>
    </div>

    {!readOnly && (
      <Button
        variant="default"
        onClick={onAdvance}
        icon={<ArrowRight className="w-4 h-4" />}
        className="w-full"
      >
        Next Step
      </Button>
    )}
  </div>
)

const ReplyButton: React.FC<{
  label: string; icon: React.ReactNode; active: boolean; onClick: () => void
  variant: 'default' | 'green' | 'red'
}> = ({ label, icon, active, onClick, variant }) => {
  const colors = {
    default: active
      ? 'bg-muted border-foreground/30 text-foreground dark:bg-white/[0.06]'
      : 'bg-background border-border/60 text-muted-foreground hover:border-foreground/20 dark:bg-white/[0.02] dark:border-white/[0.08]',
    green: active
      ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/40 dark:text-emerald-300'
      : 'bg-background border-border/60 text-muted-foreground hover:border-emerald-400/40 dark:bg-white/[0.02] dark:border-white/[0.08]',
    red: active
      ? 'bg-red-500/10 border-red-500/40 text-red-700 dark:bg-red-500/[0.12] dark:border-red-500/40 dark:text-red-400'
      : 'bg-background border-border/60 text-muted-foreground hover:border-red-400/40 dark:bg-white/[0.02] dark:border-white/[0.08]',
  }
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all ${colors[variant]}`}
    >
      {icon} {label}
    </button>
  )
}

// -- Generate Deck --
const GenerateDeckDetail: React.FC<{
  step: PlaygroundStep
  isGenerating: boolean
  onGenerate: () => void
  onAdvance: () => void
  onSkip: () => void
  onDownloadDeckPdf?: () => void
  onDownloadDeck?: () => void
  activeTab: string
  setActiveTab: (t: 'preview' | 'discussion') => void
  readOnly?: boolean
  tabIdPrefix: string
}> = ({ step, isGenerating, onGenerate, onAdvance, onSkip, onDownloadDeckPdf, onDownloadDeck, activeTab, setActiveTab, readOnly, tabIdPrefix }) => {
  const config = step.nodeData.generate_deck_config
  const deck = step.generatedDeck
  const isAttach = config?.mode === 'attach_direct'
  // Same rule as the email panel: no tab bar → the panels stay plain divs.
  const hasDiscussion = !isAttach && !!deck?.discussion?.length

  return (
    <DetailWrapper icon={<Presentation />} title={step.nodeData.label} subtitle={isAttach ? 'Attach Material' : 'Presentation Generation'}>
      {!deck ? (
        <div className="space-y-4">
          {config && !isAttach && (
            <div className="rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] p-4">
              <div className="grid grid-cols-2 gap-3">
                <InfoCard icon={<Cards className="w-3.5 h-3.5" />} label="Slides" value={String(config.slide_count || 6)} />
                <InfoCard icon={<Target className="w-3.5 h-3.5" />} label="Focus" value={config.content_focus?.replace('_', ' ')} />
                <InfoCard icon={<Palette className="w-3.5 h-3.5" />} label="Theme" value={config.color_theme?.replace('_', ' ')} />
                <InfoCard icon={<TextAa className="w-3.5 h-3.5" />} label="Tone" value={config.tone} />
              </div>
            </div>
          )}
          {isAttach && (
            <div className="rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] p-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Paperclip className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0" />
              {config?.attached_material_id ? 'Selected material will be previewed and attached — no AI is used.' : 'No material selected. Pick one in the node config.'}
            </div>
          )}
          {!readOnly && (
            <Button
              variant="default"
              onClick={onGenerate}
              loading={isGenerating}
              icon={isAttach ? <Paperclip className="w-4 h-4" /> : <Sparkle className="w-4 h-4" />}
              className="w-full h-11"
              disabled={isAttach && !config?.attached_material_id}
            >
              {isAttach ? 'Preview Material' : 'Generate Deck'}
            </Button>
          )}
          {!readOnly && (
            <Button
              variant="outline"
              onClick={onSkip}
              disabled={isGenerating}
              icon={<SkipForward className="w-4 h-4" />}
              className="w-full h-11"
            >
              Skip & Continue
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {hasDiscussion && (
            <StepTabs idPrefix={tabIdPrefix} label="Deck preview" firstTabLabel="Slides" activeTab={activeTab} onSelect={setActiveTab} />
          )}

          {activeTab === 'preview' ? (
            <div
              className="space-y-3"
              role={hasDiscussion ? 'tabpanel' : undefined}
              id={hasDiscussion ? panelDomId(tabIdPrefix, 'preview') : undefined}
              aria-labelledby={hasDiscussion ? tabDomId(tabIdPrefix, 'preview') : undefined}
            >
              <h3 className="text-sm font-medium text-foreground">{deck.title}</h3>
              <DeckPreviewArea deck={deck} step={step} />
            </div>
          ) : (
            <div
              className="space-y-3 max-h-72 overflow-y-auto"
              role="tabpanel"
              id={panelDomId(tabIdPrefix, 'discussion')}
              aria-labelledby={tabDomId(tabIdPrefix, 'discussion')}
            >
              {deck.discussion?.map((msg, i) => (
                <div key={i} className={`rounded-lg p-3 text-sm ${
                  msg.role === 'user'
                    ? 'bg-[#5e6ad2]/[0.06] border border-[#5e6ad2]/15 dark:bg-[#5e6ad2]/[0.1] dark:border-[#5e6ad2]/20'
                    : 'bg-muted/60 border border-border/50 dark:bg-white/[0.03] dark:border-white/[0.06]'
                }`}>
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-[0.06em] mb-1 block">{msg.role}</span>
                  <p className="text-foreground whitespace-pre-wrap">{msg.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Usage breakdown */}
          {step.usageLog && step.usageLog.length > 0 && <UsageBreakdown usageLog={step.usageLog} />}

          {!isAttach && (onDownloadDeckPdf || onDownloadDeck) && (
            <div className="grid grid-cols-2 gap-2">
              {onDownloadDeckPdf && (
                <Button variant="outline" onClick={onDownloadDeckPdf} icon={<Download className="w-4 h-4" />} className="w-full">
                  Download PDF
                </Button>
              )}
              {onDownloadDeck && (
                <Button variant="outline" onClick={onDownloadDeck} icon={<Download className="w-4 h-4" />} className="w-full">
                  Download PPTX
                </Button>
              )}
            </div>
          )}

          {!readOnly && (
            <Button variant="default" onClick={onAdvance} icon={<ArrowRight className="w-4 h-4" />} className="w-full">
              Next Step
            </Button>
          )}
        </div>
      )}
    </DetailWrapper>
  )
}

// -- Deck Preview Area (auto-renders PDF) --
const DeckPreviewArea: React.FC<{ deck: GeneratedDeck; step: PlaygroundStep }> = ({ deck }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const renderedRef = useRef(false)
  const isAttach = !!deck.attached_material_id

  // Auto-render PDF when deck data is available
  useEffect(() => {
    if (renderedRef.current || pdfUrl || generating) return

    // Attach Direct: fetch the material's file directly (no slide rendering).
    if (isAttach && deck.attached_material_id) {
      renderedRef.current = true
      setGenerating(true)
      materialsApi.getPreviewBlob(deck.attached_material_id)
        .then((url: string) => setPdfUrl(url))
        .catch(() => {})
        .finally(() => setGenerating(false))
      return
    }

    if (!deck.slides || deck.slides.length === 0) return

    renderedRef.current = true

    // If deck already has a PDF download_url, fetch it
    if (deck.download_url?.endsWith('.pdf')) {
      setGenerating(true)
      presentationsApi.fetchFileBlob(deck.download_url)
        .then(blob => setPdfUrl(URL.createObjectURL(blob)))
        .catch(() => {})
        .finally(() => setGenerating(false))
      return
    }

    // Otherwise render slides to PDF automatically
    setGenerating(true)
    presentationsApi.renderSlides({
      title: deck.title,
      slides: deck.slides,
      color_theme: deck.color_theme,
    })
      .then(async (res) => {
        if (res.data.download_url) {
          const blob = await presentationsApi.fetchFileBlob(res.data.download_url)
          setPdfUrl(URL.createObjectURL(blob))
        }
      })
      .catch(() => {})
      .finally(() => setGenerating(false))
  }, [deck])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl) }
  }, [pdfUrl])

  // Non-PDF attachments: browsers can't render docx/pptx inline — show a card.
  if (isAttach && deck.content_type && deck.content_type !== 'pdf' && !generating) {
    return (
      <div className="w-full rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] p-6 flex flex-col items-center justify-center gap-3 text-center">
        <FileText className="w-10 h-10 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium text-foreground">{deck.filename || deck.title}</p>
          <p className="text-xs text-muted-foreground uppercase mt-1">{deck.content_type}</p>
        </div>
        <p className="text-xs text-muted-foreground">This file type can't be previewed inline. It will be attached as-is.</p>
      </div>
    )
  }

  if (pdfUrl) {
    return (
      <iframe
        src={pdfUrl}
        className="w-full h-[500px] rounded-lg border border-border/50 dark:border-white/[0.08]"
        title={isAttach ? 'Material Preview' : 'PDF Preview'}
      />
    )
  }

  if (generating) {
    return (
      <div className="w-full h-[400px] rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] flex flex-col items-center justify-center gap-3">
        <LoadingSpinner size="md" />
        <p className="text-sm text-muted-foreground">{isAttach ? 'Loading material preview...' : 'Rendering PDF preview...'}</p>
      </div>
    )
  }

  return null
}

// -- Delay --
const DELAY_EVENT_LABELS: Record<string, string> = {
  email_opened: 'Email Opened',
  email_clicked: 'Link Clicked',
  email_replied: 'Email Replied',
}

const DelayDetail: React.FC<{ step: PlaygroundStep; onSkip: () => void; readOnly?: boolean }> = ({ step, onSkip, readOnly }) => {
  const d = step.nodeData
  const mode = d.delay_mode || 'duration'
  // Show the wait the node is actually configured for. A node switched to
  // "For Event" / "Until Time" can still carry duration-mode leftovers in
  // delay_value/delay_unit, which used to render here regardless of mode —
  // e.g. a 15-minute event timeout displayed as "30 minutes" (AB#21997).
  const display =
    mode === 'wait_for_event'
      ? {
          value: String(d.event_timeout_value ?? 3),
          caption: `${d.event_timeout_unit || 'days'} timeout`,
          note: `Waiting for ${DELAY_EVENT_LABELS[d.event_type || 'email_opened'] || 'the event'} — continues on the event path if it happens first`,
        }
      : mode === 'wait_until_time'
        ? {
            value: d.wait_until_time || '09:00',
            caption: `until this time, contact's timezone${d.business_days_only ? ' (business days)' : ''}`,
            note: undefined,
          }
        : {
            value: String(d.delay_value || '?'),
            caption: `${d.delay_unit || 'days'}${d.business_days_only ? ' (business days)' : ''}`,
            note: undefined,
          }

  return (
    <DetailWrapper icon={<Clock />} title={d.label} subtitle="Delay Step">
      <div className="space-y-4">
        <div className="rounded-lg border border-border/50 bg-card dark:bg-white/[0.02] dark:border-white/[0.08] p-6 text-center">
          <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-3 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
            <Clock className="w-6 h-6" />
          </div>
          <p className="text-3xl font-emphasis text-foreground tabular-nums">
            {display.value}
          </p>
          <p className="text-sm font-medium text-muted-foreground mt-1 capitalize">
            {display.caption}
          </p>
          {display.note && (
            <p className="text-xs text-muted-foreground mt-2">{display.note}</p>
          )}
        </div>
        {!readOnly && (
          <Button variant="default" onClick={onSkip} icon={<SkipForward className="w-4 h-4" />} className="w-full h-11">
            Skip Delay
          </Button>
        )}
      </div>
    </DetailWrapper>
  )
}

// -- Rate Limit --
// Had no case in the switch above at all, so it silently fell through to
// GenericDetail's plain "Continue" button -- nothing about the configured
// throttle (random delay range, or a per-hour/per-day cap) was ever shown
// or exercised, so testing a "Random" (or any) rate limit in the
// Playground looked like it wasn't doing anything (AB#22032). A real wait
// isn't practical in an interactive test, so this shows what the node
// would actually do and lets the user continue immediately, same pattern
// as DelayDetail's "Skip Delay".
const RateLimitDetail: React.FC<{ step: PlaygroundStep; onContinue: () => void; readOnly?: boolean }> = ({ step, onContinue, readOnly }) => {
  const config = step.nodeData.rate_limit_config
  const mode = config?.mode || 'daily_cap'
  const summary =
    mode === 'random_delay'
      ? `Random delay between ${config?.min_delay_minutes ?? 1} and ${config?.max_delay_minutes ?? 5} minutes per contact`
      : mode === 'hourly_cap'
        ? `${config?.max_per_hour ?? 20} contacts per hour, then the next batch an hour later`
        : `${config?.max_per_day ?? 50} contacts per day, then the next batch the next day`
  const modeLabel = mode === 'random_delay' ? 'Random delay' : mode === 'hourly_cap' ? 'Hourly cap' : 'Daily cap'

  return (
    <DetailWrapper icon={<Gauge />} title={step.nodeData.label} subtitle="Rate Limit">
      <div className="space-y-4">
        <div className="rounded-lg border border-border/50 bg-card dark:bg-white/[0.02] dark:border-white/[0.08] p-6 text-center">
          <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-3 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
            <Gauge className="w-6 h-6" />
          </div>
          <p className="text-sm font-emphasis text-foreground">{modeLabel}</p>
          <p className="text-xs text-muted-foreground mt-1">{summary}</p>
        </div>
        <p className="text-xs text-muted-foreground text-center">
          This contact's actual send time is spread out by the throttle above in a real
          campaign; continuing here simulates that contact clearing the rate limit.
        </p>
        {!readOnly && (
          <Button variant="default" onClick={onContinue} icon={<ArrowRight className="w-4 h-4" />} className="w-full h-11">
            Continue
          </Button>
        )}
      </div>
    </DetailWrapper>
  )
}

// -- Condition --
const ConditionDetail: React.FC<{ step: PlaygroundStep; onSelectBranch: (c: string) => void; readOnly?: boolean }> = ({ step, onSelectBranch, readOnly }) => (
  <DetailWrapper icon={<GitBranch />} title={step.nodeData.label} subtitle="Condition Branch">
    <div className="space-y-4">
      <div className="rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] p-4">
        <div className="flex items-center gap-2 mb-2">
          <GitBranch className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
          <span className="text-[11px] font-emphasis text-[#5e6ad2] dark:text-[#a5b4fc] uppercase tracking-[0.06em]">Condition</span>
        </div>
        <p className="text-sm font-medium text-foreground capitalize">{step.nodeData.condition_type?.replace(/_/g, ' ') || 'Unknown'}</p>
        {step.nodeData.condition_value && (
          <p className="text-xs text-muted-foreground mt-1">Value: {step.nodeData.condition_value}</p>
        )}
      </div>
      {readOnly && step.branchChoice && (
        <SelectedBranchLine label="Selected branch" value={step.branchChoice} capitalize />
      )}
      <p className="text-xs text-muted-foreground text-center">
        {readOnly ? 'Pick a different branch to re-route (clears later steps)' : 'Choose which branch to simulate'}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Button variant="default" onClick={() => onSelectBranch('yes')} className="w-full h-11">
          Yes / True
        </Button>
        <Button variant="outline" onClick={() => onSelectBranch('no')} className="w-full h-11">
          No / False
        </Button>
      </div>
    </div>
  </DetailWrapper>
)

// -- A/B Test --
const ABTestDetail: React.FC<{ step: PlaygroundStep; onSelectBranch: (c: string) => void; readOnly?: boolean }> = ({ step, onSelectBranch, readOnly }) => {
  const variants = step.nodeData.ab_test_config?.variants || []
  return (
    <DetailWrapper icon={<Flask />} title={step.nodeData.label} subtitle="A/B Test">
      <div className="space-y-4">
        {readOnly && step.branchChoice && (
          <SelectedBranchLine label="Selected variant" value={step.branchChoice} />
        )}
        <p className="text-xs text-muted-foreground text-center">
          {readOnly ? 'Pick a different variant to re-route (clears later steps)' : 'Choose which variant to simulate'}
        </p>
        <div className="space-y-2">
          {variants.map((v, i) => (
            <button
              key={v.id}
              onClick={() => onSelectBranch(v.id)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-border/50 bg-background hover:bg-muted/50 hover:border-[#5e6ad2]/30 transition-all group dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04]"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-emphasis bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] transition-colors">
                  {i + 1}
                </div>
                <span className="text-sm font-medium text-foreground">{v.label}</span>
              </div>
              <span className="text-xs font-mono text-muted-foreground bg-muted dark:bg-white/[0.06] px-2 py-0.5 rounded">{v.percentage}%</span>
            </button>
          ))}
          {variants.length === 0 && (
            <div className="grid grid-cols-2 gap-3">
              <Button variant="default" onClick={() => onSelectBranch('a')} className="h-11">Variant A</Button>
              <Button variant="outline" onClick={() => onSelectBranch('b')} className="h-11">Variant B</Button>
            </div>
          )}
        </div>
      </div>
    </DetailWrapper>
  )
}

// -- Branch by Field --
// Same exclusive-branch shape as A/B Test and Condition (one contact takes
// exactly one outgoing edge, matched by edge.sourceHandle === branch.id —
// see BranchByFieldNode.tsx), so it reuses the same onSelectBranch flow
// instead of the generic "Continue" button, which would fan out into every
// branch's target step and only ever let the first one become active.
type BranchOutlet = { id: string; label: string; field?: string; operator?: string; value?: string }

const BranchByFieldDetail: React.FC<{ step: PlaygroundStep; onSelectBranch: (c: string) => void; onContinue: () => void; readOnly?: boolean }> = ({ step, onSelectBranch, onContinue, readOnly }) => {
  const config = step.nodeData.branch_by_field_config
  const branches = config?.branches || []
  // Backend defaults has_default to True when the config is absent, so only
  // hide the default outlet when it is explicitly disabled (mirrors
  // BranchByFieldNode.tsx's own outlet computation).
  const hasDefault = config?.has_default !== false
  const outlets: BranchOutlet[] = [
    ...branches,
    ...(hasDefault ? [{ id: 'default', label: 'Default' }] : []),
  ]
  const selectedOutlet = outlets.find(o => o.id === step.branchChoice)

  return (
    <DetailWrapper icon={<TreeView />} title={step.nodeData.label} subtitle="Branch by Field">
      <div className="space-y-4">
        {readOnly && step.branchChoice && (
          <SelectedBranchLine label="Selected branch" value={selectedOutlet?.label || step.branchChoice} />
        )}
        <p className="text-xs text-muted-foreground text-center">
          {readOnly ? 'Pick a different branch to re-route (clears later steps)' : 'Choose which branch to simulate'}
        </p>
        <div className="space-y-2">
          {outlets.map((o, i) => (
            <button
              key={o.id}
              onClick={() => onSelectBranch(o.id)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-border/50 bg-background hover:bg-muted/50 hover:border-[#5e6ad2]/30 transition-all group dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04]"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-emphasis bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] transition-colors">
                  {i + 1}
                </div>
                <span className="text-sm font-medium text-foreground">{o.label}</span>
              </div>
              {o.field && (
                <span className="text-xs font-mono text-muted-foreground bg-muted dark:bg-white/[0.06] px-2 py-0.5 rounded">
                  {o.field} {o.operator} {o.value}
                </span>
              )}
            </button>
          ))}
          {outlets.length === 0 && (
            <p className="text-xs text-center text-muted-foreground">No branches configured on this node.</p>
          )}
        </div>
        {/* No configured outlet to click through -- without this the step is a
            dead end with no way to advance the simulation. */}
        {outlets.length === 0 && !readOnly && (
          <Button variant="default" onClick={onContinue} icon={<ArrowRight className="w-4 h-4" />} className="w-full h-11">
            Continue
          </Button>
        )}
      </div>
    </DetailWrapper>
  )
}

// -- AI Decision --
// Real branch choice depends on the LLM's read of this contact — Playground
// lets you force any branch to preview what's downstream, and TestNodePanel
// (rendered below, aiDecision is in TESTABLE_TYPES) can run the real decision.
const AiDecisionDetail: React.FC<{ step: PlaygroundStep; onSelectBranch: (c: string) => void; onContinue: () => void; readOnly?: boolean }> = ({ step, onSelectBranch, onContinue, readOnly }) => {
  const config = step.nodeData.ai_decision_config
  const branches = config?.branches || []
  return (
    <DetailWrapper icon={<Sparkle />} title={step.nodeData.label} subtitle="AI Decision">
      <div className="space-y-4">
        {config?.decision_prompt && (
          <div className="rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] p-4">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.06em]">Decision Prompt</span>
            <p className="text-sm text-foreground mt-1">{config.decision_prompt}</p>
          </div>
        )}
        <p className="text-xs text-muted-foreground text-center">
          The real branch depends on the AI's read of this contact — pick one below to preview
          that path, or use Run Test below to see what the AI actually decides.
          {readOnly && ' Picking a different branch here re-routes (clears later steps).'}
        </p>
        {readOnly && step.branchChoice && (
          <SelectedBranchLine label="Selected branch" value={step.branchChoice} capitalize />
        )}
        <div className="space-y-2">
          {branches.map((b) => (
            <button
              key={b.id}
              onClick={() => onSelectBranch(b.id)}
              className="w-full flex flex-col items-start gap-0.5 px-4 py-3 rounded-lg border border-border/50 bg-background hover:bg-muted/50 hover:border-[#5e6ad2]/30 transition-all dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04]"
            >
              <span className="text-sm font-medium text-foreground">{b.label || b.id}</span>
              {b.description && <span className="text-xs text-muted-foreground">{b.description}</span>}
            </button>
          ))}
          {branches.length === 0 && (
            <p className="text-xs text-center text-muted-foreground">No branches configured on this node.</p>
          )}
        </div>
        {/* No configured branch to click through -- without this the step is a
            dead end with no way to advance the simulation. */}
        {branches.length === 0 && !readOnly && (
          <Button variant="default" onClick={onContinue} icon={<ArrowRight className="w-4 h-4" />} className="w-full h-11">
            Continue
          </Button>
        )}
      </div>
    </DetailWrapper>
  )
}

// -- Action --
const ActionDetail: React.FC<{ step: PlaygroundStep; onContinue: () => void; readOnly?: boolean }> = ({ step, onContinue, readOnly }) => (
  <DetailWrapper icon={<Lightning />} title={step.nodeData.label} subtitle="Action">
    <div className="space-y-4">
      <div className="rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.08] p-4">
        <div className="flex items-center gap-2 mb-2">
          <Lightning className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
          <span className="text-[11px] font-emphasis text-[#5e6ad2] dark:text-[#a5b4fc] uppercase tracking-[0.06em]">Action Type</span>
        </div>
        <p className="text-sm font-medium text-foreground capitalize">{step.nodeData.action_type?.replace(/_/g, ' ') || 'Unknown'}</p>
      </div>
      {step.nodeData.action_config && (
        <div className="bg-muted/50 dark:bg-white/[0.03] rounded-lg p-3 text-xs font-mono overflow-x-auto border border-border/50 dark:border-white/[0.08] max-h-32">
          {JSON.stringify(step.nodeData.action_config, null, 2)}
        </div>
      )}
      {!readOnly && (
        <Button variant="default" onClick={onContinue} icon={<ArrowRight className="w-4 h-4" />} className="w-full h-11">
          Continue
        </Button>
      )}
    </div>
  </DetailWrapper>
)

// -- Enrich --
const EnrichDetail: React.FC<{ step: PlaygroundStep; onContinue: () => void; readOnly?: boolean; enriched?: boolean }> = ({ step, onContinue, readOnly, enriched }) => (
  <DetailWrapper icon={<Users />} title={step.nodeData.label} subtitle="Enrich Contacts">
    <div className="space-y-4">
      <div className="rounded-lg border border-border/50 bg-card dark:bg-white/[0.02] dark:border-white/[0.08] p-5 text-center">
        <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-3 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
          <Users className="w-6 h-6" />
        </div>
        <p className="text-sm font-medium text-foreground mb-1">Enrichment Step</p>
        <p className="text-xs text-muted-foreground">
          {enriched
            ? 'Enrichment ran for this contact. Continue to the next step.'
            : 'In a live campaign, this enriches contact data via AI. Skipped in playground unless you run it below.'}
        </p>
      </div>
      {!readOnly && (
        <Button variant="default" onClick={onContinue} icon={<ArrowRight className="w-4 h-4" />} className="w-full h-11">
          {enriched ? 'Next step' : 'Skip & Continue'}
        </Button>
      )}
    </div>
  </DetailWrapper>
)

// -- Goal --
const GoalDetail: React.FC<{ step: PlaygroundStep }> = ({ step }) => (
  <DetailWrapper icon={<Target />} title={step.nodeData.label} subtitle="Goal Reached">
    <div className="rounded-lg border border-border/50 bg-card dark:bg-white/[0.02] dark:border-white/[0.08] p-6 text-center">
      <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:bg-emerald-500/[0.12] dark:border-emerald-500/25 dark:text-emerald-400">
        <Target className="w-6 h-6" />
      </div>
      <p className="text-base font-emphasis text-emerald-700 dark:text-emerald-400 mb-1">Goal Reached</p>
      <p className="text-sm text-muted-foreground capitalize">
        {step.nodeData.goal_config?.goal_type?.replace(/_/g, ' ') || 'Conversion achieved'}
      </p>
    </div>
  </DetailWrapper>
)

// -- Generic fallback --
// Node types with no dedicated Playground preview above. Split into two
// groups so the label tells the truth about what's actually available:
// - PLAYGROUND_TESTABLE: TestNodePanel (rendered below, see PlaygroundStepDetail)
//   can run this node for real against the selected contact — no dedicated
//   preview card here, but "Run Test" below isn't a dead end.
// - everything else: no real execution is possible outside a live campaign;
//   this step is purely a pass-through in the walkthrough.
const PLAYGROUND_TESTABLE_FALLBACK_TYPES = new Set([
  'httpRequest', 'internalAlert', 'smsMessage', 'linkedInAction',
])

const GENERIC_NODE_LABELS: Record<string, string> = {
  httpRequest: 'HTTP Request',
  internalAlert: 'Team Alert',
  smsMessage: 'Send SMS',
  linkedInAction: 'LinkedIn Action',
  action: 'Action',
  exitWorkflow: 'Exit Workflow',
  createTask: 'Create Task',
  rateLimit: 'Rate Limit',
  assignOwner: 'Assign Owner',
  crmSync: 'CRM Sync',
  scheduleMeeting: 'Schedule Meeting',
}

const GenericDetail: React.FC<{ step: PlaygroundStep; onContinue: () => void; readOnly?: boolean }> = ({ step, onContinue, readOnly }) => {
  const isTestable = PLAYGROUND_TESTABLE_FALLBACK_TYPES.has(step.nodeType)
  const label = GENERIC_NODE_LABELS[step.nodeType] || step.nodeType.replace(/_/g, ' ')
  return (
    <DetailWrapper icon={<FileText />} title={step.nodeData.label} subtitle={label}>
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.08] dark:bg-amber-500/[0.1] p-4">
          <p className="text-sm font-medium text-foreground mb-1">Not simulated in the Playground</p>
          <p className="text-xs text-muted-foreground">
            {isTestable
              ? `"${label}" has no walkthrough preview here — its real effect depends on runtime config. Use "Run Test" below to try it for real against the selected contact.`
              : `"${label}" can't be previewed or tested individually. It always runs for real during a live campaign send — click Continue to move past it in this walkthrough.`}
          </p>
        </div>
        {!readOnly && (
          <Button variant="default" onClick={onContinue} icon={<ArrowRight className="w-4 h-4" />} className="w-full h-11">
            Continue
          </Button>
        )}
      </div>
    </DetailWrapper>
  )
}

// -- Usage Breakdown --
const UsageBreakdown: React.FC<{ usageLog: UsageEntry[] }> = ({ usageLog }) => (
  <div className="border border-border/50 dark:border-white/[0.08] rounded-lg p-3 bg-muted/30 dark:bg-white/[0.02] space-y-2">
    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.06em]">Token Usage</p>
    {usageLog.map((entry, i) => (
      <div key={i} className="flex items-center justify-between text-xs">
        <span className="text-foreground font-medium">{entry.agent}</span>
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground font-mono text-[11px]">
            {entry.input_tokens.toLocaleString()} in / {entry.output_tokens.toLocaleString()} out
          </span>
          <span className="text-foreground font-mono font-medium text-[11px] tabular-nums">
            ${entry.cost.toFixed(4)}
          </span>
        </div>
      </div>
    ))}
    <div className="border-t border-border/50 dark:border-white/[0.06] pt-1.5 flex items-center justify-between text-xs font-emphasis">
      <span className="text-foreground">Total</span>
      <div className="flex items-center gap-3">
        <span className="text-muted-foreground font-mono text-[11px]">
          {usageLog.reduce((s, u) => s + u.input_tokens + u.output_tokens, 0).toLocaleString()} tokens
        </span>
        <span className="text-foreground font-mono text-[11px] tabular-nums">
          ${usageLog.reduce((s, u) => s + u.cost, 0).toFixed(4)}
        </span>
      </div>
    </div>
  </div>
)

// -- Deck Attachment Badge --
const DeckAttachmentBadge: React.FC<{ deck: GeneratedDeck }> = ({ deck }) => (
  <div className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/50 dark:bg-white/[0.03] dark:border-white/[0.08] px-3 py-2 mt-3">
    <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
      <Presentation className="w-4 h-4" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-foreground truncate">{deck.title || 'Presentation'}.pptx</p>
      <p className="text-xs text-muted-foreground">{deck.slides.length} slides &middot; Will be attached</p>
    </div>
  </div>
)

// -- Shared wrapper --
const DetailWrapper: React.FC<{
  icon: React.ReactNode; title: string; subtitle: string; children: React.ReactNode
}> = ({ icon, title, subtitle, children }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.25, ease: 'easeOut' }}
    className="p-6"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
        {icon}
      </div>
      <div className="min-w-0">
        <h2 className="text-lg font-emphasis text-foreground leading-tight truncate">{title}</h2>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </div>
    {children}
  </motion.div>
)

// -- Small helpers --
const InfoCard: React.FC<{ icon: React.ReactNode; label: string; value?: string }> = ({ icon, label, value }) => (
  <div className="flex items-center gap-2.5 rounded-lg bg-background border border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08] px-3 py-2">
    <div className="h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[10px] text-muted-foreground uppercase tracking-[0.06em] leading-none">{label}</p>
      <p className="text-sm font-medium text-foreground capitalize truncate mt-0.5">{value || '—'}</p>
    </div>
  </div>
)
